/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ITableAllProps, type ITableProps, SortingMode, Table } from 'ka-table';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { type AttributeTableData } from 'ka-table/models';
import classNames from 'classnames';
import { type TRPCClientErrorLike } from '@trpc/client';
import { useTranslation } from 'react-i18next';
import { type UseTRPCInfiniteQueryResult } from '@trpc/react-query/shared';

import { type Obj } from '~/common/types';
import { type SortSettings } from '~/common/types/sort';
import { type UseToggle } from '@/shared/hooks/UseToggle';

import { AppContext } from '@/shared/context/context';

import { getStoredColumns, saveColumns } from '@/shared/utils/helpers';

import useToggleHeight from '@/shared/hooks/useToggleHeight';

import DataTableFilter from '@/shared/components/2.0/DataTableFilter';
import DataTableSearch from '@/shared/components/2.0/DataTableSearch';
import DefaultHeaderCell from '@/shared/components/2.0/customTableCells/DefaultHeaderCell';
import DefaultTableCell from '@/shared/components/2.0/customTableCells/DefaultTableCell';
import MultiSelectColumns from '@/shared/components/2.0/MultiSelectColumns';
import signalRMessages from '@/redux/actions/signalRMessages';
import SortableHeadCell from '@/shared/components/2.0/customTableCells/SortableHeaderCell';
import TabsMenu from '@/shared/components/2.0/layout/Tabs/menu/TabsMenu';

const LOAD_MORE_THRESHOLD_PX = 50;

type ScrollableTableProps<T> = {
  fetchResult: UseTRPCInfiniteQueryResult<
    {
      items: T[];
      nextPage?: number | null;
      totalCount?: number;
      page?: number;
    },
    TRPCClientErrorLike<any>
  >;
  config: {
    columns: ITableAllProps['columns'];
    localStorageKey: string;
    rowId: string;
    groups?: ITableAllProps['groups'];
    rowKeyField: ITableAllProps['rowKeyField'];
    childComponents?: ITableAllProps['childComponents'];
    sortingMode?: ITableAllProps['sortingMode'];
    type?: string;
  };
  title?: string;
  toggle?: UseToggle;
  filter: React.ReactNode | null;
  setSelected?: (row: T) => void;
  selectedItems?: T[];
  sortableColumns?: string[];
  setSortSettings?: React.Dispatch<React.SetStateAction<SortSettings[]>>;
  sortSettings?: SortSettings[];
  setSearchText?: (searchText: string) => void;
  offsetY?: number;
  hideBackground?: boolean;
  columnResizing?: boolean;
  columnReordering?: boolean;
  filterLocalStorageKey?: string;
  customActions?: React.ReactNode;
  fixedSizeForSearchBar?: boolean;
  isCompact?: boolean;
  usePadding?: boolean;
  searchTerm?: string;
  cellContentProps?: Obj;
  noDataRowContent?: React.ReactNode;
  showColumnSelection?: boolean;
};

const PaginatedTable = <T,>({
  fetchResult,
  config,
  setSelected,
  sortableColumns,
  setSortSettings,
  sortSettings,
  title,
  toggle,
  filter,
  setSearchText,
  offsetY,
  hideBackground = false,
  columnResizing = false,
  columnReordering = false,
  filterLocalStorageKey,
  customActions,
  fixedSizeForSearchBar = false,
  isCompact = false,
  selectedItems = [],
  usePadding = true,
  cellContentProps,
  noDataRowContent,
  searchTerm,
  showColumnSelection = true,
}: ScrollableTableProps<T>) => {
  const {
    data: pagedData,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetching,
    refetch,
  } = fetchResult;

  const data = useMemo(() => {
    if (!pagedData?.pages) {
      return [];
    }
    return pagedData.pages
      .flatMap((page) => page.items)
      .map((item, index) => ((item as { id: string }).id ? item : { ...item, id: index }));
  }, [pagedData]);

  const { socket } = useContext(AppContext);

  useEffect(() => {
    socket?.on(signalRMessages.ReconciliationRequestCompleted, () => refetch());
    return () => {
      socket?.off(signalRMessages.ReconciliationRequestCompleted);
    };
  }, [refetch, socket]);

  const { t } = useTranslation();
  const { columns, localStorageKey, rowId, groups, rowKeyField, childComponents, sortingMode } =
    config;

  const [tableProps, setTableProps] = useState({
    columnReordering,
    columnResizing,
    columns: getStoredColumns(columns ?? [], localStorageKey),
    groups,
    localStorageKey,
    rowId,
    rowKeyField,
  });

  const dispatch = useCallback(
    (action: any) => {
      setTableProps((prevState) =>
        saveColumns({ action, localStorageType: localStorageKey, prevState }),
      );
    },
    [localStorageKey],
  );

  const handleScrollMore = (e: React.SyntheticEvent, options: AttributeTableData<ITableProps>) => {
    const { baseFunc } = options;
    baseFunc(e);
    const tableWrapper = e.currentTarget as HTMLElement;
    const scrollMore =
      tableWrapper.scrollHeight - tableWrapper.scrollTop - tableWrapper.clientHeight <
      LOAD_MORE_THRESHOLD_PX;
    if (!isLoading && !isFetchingNextPage && hasNextPage && scrollMore) {
      fetchNextPage();
    }
  };

  const filterToggle = useToggleHeight({
    localStorageKey: filterLocalStorageKey ?? localStorageKey,
    offsetY: offsetY ?? 300,
  });

  // isLoading is true for a disabled query (it only means "no data yet"), so a
  // query that is disabled until required filters are set would show a
  // perpetual spinner - isFetching reflects whether a fetch is actually running.
  const dataIsLoading = isFetching || isFetchingNextPage;

  return (
    <div
      className={classNames(
        !hideBackground && classNames('rounded-xl', isCompact ? 'bg-gray-100' : 'bg-white'),
        isCompact ? 'rounded-2xl p-0' : usePadding ? 'p-2 pt-0' : 'pt-0',
      )}
    >
      <TabsMenu.Actions className={isCompact ? 'mb-2' : 'mb-4'}>
        {!!title && (
          <div className="grow">
            <h4>{title}</h4>
          </div>
        )}
        {setSearchText && (
          <DataTableSearch
            className="flex-1"
            fixed={fixedSizeForSearchBar}
            searchTerm={searchTerm}
            setSearchText={(searchText) => {
              setSearchText?.(searchText);
            }}
          />
        )}
        <div className="flex flex-row gap-2">
          {filter && <DataTableFilter toggle={filterToggle} />}
          {customActions}
          {showColumnSelection && (
            <MultiSelectColumns
              columns={tableProps.columns}
              type={config.type || ''}
              onSelectFilter={dispatch}
            />
          )}
        </div>
      </TabsMenu.Actions>

      {filter && filterToggle.value && (
        <div ref={filterToggle.divRef} className={classNames(isCompact ? 'mb-1' : 'mb-4')}>
          {filter}
        </div>
      )}
      <div className={classNames('rounded-2xl border border-gray-200 bg-gray-100')}>
        <Table
          {...tableProps}
          childComponents={{
            ...childComponents,
            cell: {
              content: (props) =>
                DefaultTableCell.DefaultTableCell({
                  rowId,
                  selectedItems,
                  setSelected,
                  t,
                  toggle,
                  ...cellContentProps,
                  ...props,
                }),
            },
            headCell: {
              content: (props) => {
                if (
                  sortableColumns &&
                  setSortSettings &&
                  sortableColumns.includes(props.column.key)
                ) {
                  return SortableHeadCell.SortableHeadCell({
                    setSortSettings,
                    sortSettings: sortSettings ?? [],
                    t,
                    type: config.type || null,
                    ...props,
                  });
                }

                return DefaultHeaderCell.DefaultHeadCell({
                  t,
                  type: config.type || null,
                  ...props,
                });
              },
            },
            noDataRow: !dataIsLoading
              ? { content: () => noDataRowContent || t('noDataToDisplay') }
              : undefined,
            tableWrapper: {
              elementAttributes: () => ({
                onScroll: handleScrollMore,
              }),
            },
          }}
          data={data}
          dispatch={dispatch}
          height={filterToggle.height}
          loading={{ enabled: dataIsLoading }}
          sortingMode={sortingMode ?? SortingMode.None}
        />
      </div>
    </div>
  );
};

export default PaginatedTable;
